import { useState, useEffect, Fragment } from "react";
import {
  Dialog,
  Transition,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  Alert,
  AlertFormData,
  AlertUpdateDto,
  AlertCondition,
} from "../../../types/alert";
import AlertForm from "./AlertForm";
import { useUpdateAlert } from "../../../queries/alerts";
import { toast } from "react-toastify";
import { queryClient } from "../../../queries/client";

interface EditAlertDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  alert: Alert;
  onSave: (alert: Alert) => void;
}

export default function EditAlertDrawer({
  isOpen,
  onClose,
  alert,
  onSave,
}: EditAlertDrawerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState<AlertFormData | null>(
    null,
  );
  const { mutate: updateAlert } = useUpdateAlert();

  useEffect(() => {
    if (alert) {
      // Convert Alert to AlertFormData for the form
      const formData: AlertFormData = {
        name: alert.name,
        condition: alert.condition,
        threshold: alert.threshold.toString(),
        thresholdMax: alert.thresholdMax ? alert.thresholdMax.toString() : "",
        primaryCooldown: alert.primaryCooldown.toString(),
        cooldown: alert.cooldown.toString(),
        primaryEmails: alert.primaryEmails || [],
        secondaryEmails: alert.secondaryEmails || [],
        parameterId: alert.parameterId,
      };
      setInitialFormData(formData);
    }
  }, [alert]);

  const handleSubmit = async (formData: AlertFormData) => {
    setIsLoading(true);
    try {
      const alertUpdateDto: AlertUpdateDto = {
        name: formData.name,
        condition: formData.condition,
        threshold: parseFloat(formData.threshold),
        thresholdMax: formData.thresholdMax
          ? parseFloat(formData.thresholdMax)
          : undefined,
        primaryCooldown: parseInt(formData.primaryCooldown) || 60,
        cooldown: parseInt(formData.cooldown) || 1800,
        primaryEmails: formData.primaryEmails,
        secondaryEmails: formData.secondaryEmails,
        parameterId: formData.parameterId,
      };

      updateAlert(
        { data: alertUpdateDto, alertId: alert.id },
        {
          onSuccess: () => {
            toast("Alert updated successfully", {
              type: "success",
              autoClose: 2000,
            });

            queryClient.invalidateQueries({
              queryKey: ["get-location-devices"],
            });
            onClose();
          },
          onError: (error: any) => {
            toast(
              error?.response?.data?.errorMessage || "Failed to update alert",
              {
                type: "error",
                autoClose: 2000,
              },
            );
          },
        },
      );

      const updatedAlert: Alert = {
        ...alert,
        ...alertUpdateDto,
        updatedAt: new Date().toISOString(),
      };

      onSave(updatedAlert);
      onClose();
    } catch (error) {
      console.error("Error updating alert:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!initialFormData) {
    return null;
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col bg-white dark:bg-gray-900 shadow-xl">
                    {/* Header */}
                    <div className="px-4 py-6 sm:px-6 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-lg font-semibold text-gray-900 dark:text-white">
                            Edit Alert
                          </div>
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Update alert configuration and settings
                          </p>
                        </div>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md bg-white dark:bg-gray-900 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            onClick={onClose}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="relative flex-1 px-4 sm:px-6 overflow-y-auto">
                      <div className="py-6">
                        <AlertForm
                          mode="edit"
                          initialData={initialFormData}
                          onSubmit={handleSubmit}
                          onCancel={handleCancel}
                          isLoading={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
